import React, { useEffect, useState } from "react";
import { ProjectCard } from "../projectcard";
import Loader from "../loader";
import LazyOnScroll from "../../ui/LazyOnScroll";
import projectsAll from "../../data/projects_all.json";
import industrial from "../../data/industrial.json";
import commercial from "../../data/commercial.json";
import residential from "../../data/residential.json";
import institutional from "../../data/institutional.json";
import Helmet from "react-helmet";

interface Project {
  _id: string;
  title: string;
  category: string;
  location: string;
  shortDescription: string;
  coverImage: string;
}

const CATEGORIES = [
  { label: "All Projects", value: "projects_all" },
  { label: "Industrial", value: "industrial" },
  { label: "Commercial", value: "commercial" },
  { label: "Residential", value: "residential" },
  { label: "Institutional", value: "institutional" },
];

const staticProjectMap: Record<string, Project[]> = {
  projects_all: projectsAll,
  industrial,
  commercial,
  residential,
  institutional,
};

export default function PortfolioPage() {
  const [selectedTab, setSelectedTab] = useState("projects_all");
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    let loaderTimer: NodeJS.Timeout;
    setLoading(true);
    loaderTimer = setTimeout(() => setShowLoader(true), 300);

    const data = staticProjectMap[selectedTab] || [];

    setTimeout(() => {
      setProjects(data);
      setLoading(false);
      setShowLoader(false);
      clearTimeout(loaderTimer);
    }, 100);

    return () => clearTimeout(loaderTimer);
  }, [selectedTab]);

  return (
    <>
      <Helmet>
        <title>Our Portfolio – Walls | Forming a Thinking Through Design</title>
        <meta
          name="description"
          content="Explore our architectural portfolio - walls forming a thinking through innovative residential, commercial, industrial, and institutional designs. See how our walls create spaces that inspire."
        />
        <meta name="keywords" content="walls portfolio, architectural walls, forming a thinking, wall design projects, residential walls, commercial walls" />
        <link rel="canonical" href="https://www.wallsconcepts.com/portfolio" />
        <meta property="og:title" content="Portfolio - Walls Forming a Thinking" />
        <meta property="og:description" content="Explore our architectural portfolio showcasing walls that form thinking through innovative design." />
        <meta property="og:url" content="https://www.wallsconcepts.com/portfolio" />
        <meta property="og:image" content="https://www.wallsconcepts.com/logo.jpg" />
      </Helmet>
      <div className="flex min-h-screen flex-col">
        <main className="flex-1">
          {/* Static Intro Section */}
          <LazyOnScroll>
            <section className="py-16 md:py-24 bg-gradient-to-b from-white to-[#f9faf1]">
              <div className="container max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-12 xl:px-20 text-center flex flex-col items-center">
                <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-[#484331]">
                  Our Portfolio - Walls Forming a Thinking
                </h1>
                <p className="mt-4 max-w-[700px] text-[#484331]/80 md:text-xl">
                  Discover how our walls form thinking through diverse architectural projects. Each wall tells a story, each space inspires thought – from residential walls to commercial innovations.
                </p>
              </div>
            </section>
          </LazyOnScroll>

          {/* Dynamic Projects Section */}
          <section className="py-16 md:py-24">
            <div className="container max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-12 xl:px-20">
              {/* Tabs */}
              <div className="flex justify-center mb-8 text-[#484331]">
                <div className="overflow-x-auto">
                  <div className="flex flex-wrap justify-center gap-2 items-center rounded-xl p-2 bg-[#f9faf1]">
                    {CATEGORIES.map(({ label, value }) => (
                      <button
                        key={value}
                        onClick={() => setSelectedTab(value)}
                        className={`inline-flex h-[calc(100%-1px)] items-center justify-center gap-1.5 rounded-md border border-transparent px-3 py-1 text-sm font-medium whitespace-nowrap transition-colors ${
                          selectedTab === value
                            ? "bg-white shadow-sm text-black"
                            : "text-gray-600 hover:bg-gray-300"
                        }`}
                        type="button"
                        role="tab"
                        aria-selected={selectedTab === value}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Project Cards */}
              <div className="flex-1 outline-none mt-0">
                {loading && showLoader ? (
                  <Loader />
                ) : loading && !showLoader ? (
                  <div style={{ minHeight: "30vh" }}></div>
                ) : projects.length === 0 ? (
                  <p className="text-center text-muted-foreground">
                    No projects found.
                  </p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {projects.map((project, index) => (
                      <LazyOnScroll key={project._id} delay={index * 60}>
                        <ProjectCard
                          project={{
                            ...project,
                            image: project.coverImage,
                            description: project.shortDescription,
                            year: "",
                          }}
                        />
                      </LazyOnScroll>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
